import pluralize from 'pluralize';
import { memo, useCallback } from 'react';
import { useSelector } from 'react-redux';

import { BoardAddIcon } from '@air/design-icons';
import {
  contentCollectionAllUploadsCompletedSelector,
  contentCollectionFieldsSelector,
} from '@air/feature-content-collection/redux';
import { FileStatus } from '@air/feature-file-status/constants';
import { FileStatusIcon } from '@air/feature-file-status/ui';
import { UploadPreviewLoaders, UploadsTrackingPanel, type UploadsTrackingPanelProps } from '@air/feature-uploads/ui';
import { formatEstimatedCompletionTime, getUploadHeaderTitles } from '@air/feature-uploads/utils';
import { useAirModal } from '@air/provider-modal';
import { useDeleteNonCompletedUploads } from '@air/upload-utils';
import { cn } from '@air/utils-tailwind';

import { ContentCollectionCancelAllUploadsModal } from '~/components/ContentCollection/ContentCollectionCancelAllUploadsModal';
import { useContentCollectionUploadDelete } from '~/components/ContentCollection/hooks/useContentCollectionUploadDelete';

export type ContentCollectionUploadsTrackingPanelProps = Pick<UploadsTrackingPanelProps, 'className'>;

export const ContentCollectionUploadsTrackingPanel = memo(
  ({ className }: ContentCollectionUploadsTrackingPanelProps) => {
    const formFields = useSelector(contentCollectionFieldsSelector);
    const allUploadsCompleted = useSelector(contentCollectionAllUploadsCompletedSelector);
    const { onDeleteUpload: onDelete } = useContentCollectionUploadDelete();

    const hasRequiredFields = formFields.some((field) => field.required && field.type !== 'title');

    const [showCancelAllUploadsModal, closeCancelAllUploadsModal] = useAirModal(ContentCollectionCancelAllUploadsModal);

    const { deleteAllNonCompletedUploads } = useDeleteNonCompletedUploads({ onDelete });

    const getStatusTitle = useCallback<NonNullable<UploadsTrackingPanelProps['getStatusTitle']>>(
      (args) => {
        const { counts, status, estimatedCompletionTime } = args;
        const { formattedCompletionTime } = formatEstimatedCompletionTime(estimatedCompletionTime);

        switch (status) {
          case FileStatus.stopped:
            return {
              title: 'Upload stopped',
              subtitle: 'No longer accepting submissions',
            };
          case FileStatus.progressing: {
            return {
              title: `${hasRequiredFields ? 'Readying' : 'Adding'} ${pluralize('items', counts.progressingCount, true)}...`,
              ...(formattedCompletionTime && { subtitle: `Estimated time: ${formattedCompletionTime}` }),
            };
          }
          case FileStatus.completed:
            return {
              title: "You're ready to send your files!",
              subtitle: `${pluralize('items', counts.completedCount, true)} added`,
            };
        }

        return getUploadHeaderTitles(args)[status];
      },
      [hasRequiredFields],
    );

    const getSummaryTrackingAvatar = useCallback<NonNullable<UploadsTrackingPanelProps['getSummaryTrackingAvatar']>>(
      (_, status) => {
        if (status === FileStatus.completed) {
          return (
            <div aria-hidden className="mr-3 flex size-10 items-center justify-center rounded-full bg-teal-2">
              <div className="flex items-center justify-center">
                <BoardAddIcon className="text-green-10" size="lg" />
              </div>
            </div>
          );
        }

        return <FileStatusIcon status={status} />;
      },
      [],
    );

    const shouldHideTrackingPanel = hasRequiredFields && allUploadsCompleted;

    return (
      <>
        <UploadPreviewLoaders />
        {shouldHideTrackingPanel ? null : (
          <UploadsTrackingPanel
            className={cn('!w-full md:!w-[373px]', className)}
            summaryLocation={undefined}
            // biome-ignore lint/performance/noJsxPropsBind: PLA-1724 — pre-existing debt, not introduced by the icon migration
            onCancelAllUploads={() => {
              showCancelAllUploadsModal({
                onCancelUpload: () => {
                  // biome-ignore lint/nursery/noFloatingPromises: PLA-1724 — pre-existing debt, not introduced by the icon migration
                  deleteAllNonCompletedUploads();
                  closeCancelAllUploadsModal();
                },
              });
            }}
            onDeleteUpload={onDelete}
            disableDismiss
            getStatusTitle={getStatusTitle}
            getSummaryTrackingAvatar={getSummaryTrackingAvatar}
          />
        )}
      </>
    );
  },
);

ContentCollectionUploadsTrackingPanel.displayName = 'ContentCollectionUploadsTrackingPanel';
